import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

const OrderSummary = () => {
  const navigate = useNavigate();
  const { selectedClass, enrollmentData } = useSelector((state) => state.class);
  
  // Get data from Redux - use useMemo to prevent re-renders
  const students = useMemo(() => enrollmentData?.students || [], [enrollmentData?.students]);
  const totalFee = enrollmentData?.totalFee || 0;
  
  // Calculate fee per student
  const feePerStudent = selectedClass?.class_Fee || 240;
  const numberOfStudents = students.length;
  
  useEffect(() => {
    // Redirect if no enrollment data in Redux
    if (!enrollmentData || !students || students.length === 0) {
      navigate('/class/enroll-student');
    }
  }, [enrollmentData, students, navigate]);
  
  const handleBack = () => {
    navigate(-1);
  };
  
  const handlePayment = () => {
    // Navigate to payment page - data persists in Redux
    navigate('/class/payment');
  };
  
  if (!enrollmentData || !students || students.length === 0) {
    return null;
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 py-16">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Header */}
        <div className="mb-12">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">
                Order Summary
              </h1>
              <p className="text-gray-600">Review your class enrollment details</p>
            </div>
            
            <button
              onClick={handleBack}
              className="bg-red-500 hover:bg-red-600 text-white font-semibold py-2 px-6 rounded-full transition shadow-md hover:shadow-lg flex items-center gap-2"
            >
              ← Back
            </button>
          </div>
        </div>
        
        {/* Order Summary Card */}
        <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-2xl p-8 mb-8 border border-gray-100">
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-gradient-to-br from-blue-500 to-purple-600 rounded-full p-3 shadow-lg">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800">Enrollment Details</h2>
          </div>
          
          <div className="space-y-4">
            {/* Class Name */}
            <div className="flex justify-between items-start py-4 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                <span className="text-gray-700 font-semibold">Class Name</span>
              </div>
              <span className="text-gray-800 font-medium text-right max-w-md">
                {selectedClass?.class_Name || 'Yoga And Pranayam - Single Yearly - 2026'}
              </span>
            </div>
            
            {/* Students List */}
            <div className="py-4 border-b border-gray-200">
              <div className="flex items-center gap-2 mb-3">
                <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <span className="text-gray-700 font-semibold">Students Enrolled ({numberOfStudents})</span>
              </div>
              <div className="ml-7 space-y-2">
                {students.map((student, index) => {
                  const firstName = student.student_First_Name || student.firstName || '';
                  const lastName = student.student_Last_Name || student.lastName || '';
                  const gender = (student.student_Gender || student.gender || '').toLowerCase();
                  const genderIcon = gender === 'male' || gender === 'm' ? '👦' : gender === 'female' || gender === 'f' ? '👧' : '👤';
                  
                  return (
                    <div key={index} className="flex items-center gap-3 bg-blue-50 px-4 py-3 rounded-lg">
                      <span className="text-2xl">{genderIcon}</span>
                      <div className="flex-1">
                        <p className="text-gray-800 font-semibold">{firstName} {lastName}</p>
                        <p className="text-gray-500 text-sm">${feePerStudent.toFixed(2)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            
            {/* Total Fee */}
            <div className="flex justify-between items-center py-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl px-6 mt-4">
              <div className="flex items-center gap-2">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-gray-800 font-bold text-lg">Total Amount</span>
              </div>
              <span className="text-green-600 font-bold text-3xl">${totalFee.toFixed(2)}</span>
            </div>
          </div>
        </div>
        
        {/* Payment Button */}
        <div className="flex justify-end">
          <button
            onClick={handlePayment}
            className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-bold py-4 px-10 rounded-full transition-all duration-300 shadow-lg hover:shadow-2xl transform hover:-translate-y-1 flex items-center gap-3"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
            </svg>
            Proceed to Payment
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderSummary;
